import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
  Chip,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Event,
  List,
  Notifications,
  Store,
  Favorite,
  AccountBalanceWallet,
  Share,
  Help,
  Info,
  Settings,
  Logout,
  ExpandMore,
  ExpandLess,
  PrivacyTip,
  Description,
  Schedule,
  AssignmentReturn,
  LocationOn,
  CreditCard,
  Lock,
  Warning
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextNew';
import { useCart } from '../contexts/CartContext';

const ProfileGridMenu = () => {
  const navigate = useNavigate();
  const { user, logout, isGuest } = useAuth();
  const { clearCart, totalItems } = useCart();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const handleLogoutClick = () => {
    // Check if user has items in cart
    if (totalItems > 0) {
      setShowLogoutDialog(true);
    } else {
      performLogout();
    }
  };

  const performLogout = async () => {
    try {
      // Clear cart first
      clearCart();
      
      // Then logout (this will also clear cart from localStorage)
      await logout(true);
      
      // Navigate to login page
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleCancelLogout = () => {
    setShowLogoutDialog(false);
  };

  const menuItems = [
    // Primary Actions Group
    {
      id: 'bookings',
      title: 'Bookings',
      icon: <Event />,
      group: 'Primary Actions',
      onPress: () => navigate('/bookings'),
    },
    {
      id: 'orders',
      title: 'Product Orders',
      icon: <List />,
      group: 'Primary Actions',
      onPress: () => navigate('/product-orders'),
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: <Notifications />,
      group: 'Primary Actions',
      onPress: () => navigate('/notifications'),
    },
    {
      id: 'store',
      title: 'Store',
      icon: <Store />,
      group: 'Primary Actions',
      onPress: () => navigate('/store'),
    },
    {
      id: 'favorites',
      title: 'Favourites',
      icon: <Favorite />,
      group: 'Primary Actions',
      onPress: () => navigate('/favorites'),
    },
    {
      id: 'wallet',
      title: 'Wallet',
      icon: <AccountBalanceWallet />,
      group: 'Primary Actions',
      onPress: () => navigate('/wallet'),
    },
    {
      id: 'refer-earn',
      title: 'Refer & Earn',
      icon: <Share />,
      group: 'Primary Actions',
      onPress: () => navigate('/referral'),
    },
    {
      id: 'faq',
      title: 'FAQ',
      icon: <Help />,
      group: 'Primary Actions',
      onPress: () => navigate('/faq'),
    },
    // Informations Group
    {
      id: 'informations',
      title: 'Informations',
      icon: <Info />,
      group: 'Informations',
      onPress: () => setExpandedMenu(expandedMenu === 'informations' ? null : 'informations'),
      subItems: [
        {
          id: 'privacy-policy',
          title: 'Privacy Policy',
          icon: <PrivacyTip />,
          onPress: () => navigate('/privacy-policy'),
        },
        {
          id: 'terms-conditions',
          title: 'Terms & Conditions',
          icon: <Description />,
          onPress: () => navigate('/terms-conditions'),
        },
        {
          id: 'reschedule-cancellation',
          title: 'Reschedule & Cancellation Policy',
          icon: <Schedule />,
          onPress: () => navigate('/reschedule-cancellation-policy'),
        },
        {
          id: 'product-cancellation-refund',
          title: 'Product Cancellation & Refund Policy',
          icon: <AssignmentReturn />,
          onPress: () => navigate('/product-cancellation-refund-policy'),
        },
        {
          id: 'about-us',
          title: 'About Us',
          icon: <Info />,
          onPress: () => navigate('/about-us'),
        },
      ],
    },
    // Account Actions Group
    {
      id: 'addresses',
      title: 'My Addresses',
      icon: <LocationOn />,
      group: 'Account Actions',
      onPress: () => navigate('/addresses'),
    },
    // Payment Methods temporarily hidden
    // {
    //   id: 'payment-methods',
    //   title: 'Payment Methods',
    //   icon: <CreditCard />,
    //   group: 'Account Actions',
    //   onPress: () => navigate('/payment-methods'),
    // },
    {
      id: 'change-password',
      title: 'Change Password',
      icon: <Lock />,
      group: 'Account Actions',
      onPress: () => navigate('/change-password'),
    },
    {
      id: 'logout',
      title: 'Logout',
      icon: <Logout />,
      group: 'Account Actions',
      onPress: handleLogoutClick,
    },
  ];

  const groupedMenuItems = menuItems.reduce((groups, item) => {
    if (!groups[item.group]) {
      groups[item.group] = [];
    }
    groups[item.group].push(item);
    return groups;
  }, {});

  const renderMenuItem = (item) => (
    <Card
      key={item.id}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: theme.shadows[4],
          backgroundColor: item.id === 'logout' ? '#d32f2f' : '#0A2A66',
          '& .menu-icon': {
            color: '#ffffff',
          },
          '& .menu-text': {
            color: '#ffffff',
          },
          '& .expand-icon': {
            color: '#ffffff',
          },
        },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      onClick={() => {
        if (item.subItems) {
          setExpandedMenu(expandedMenu === item.id ? null : item.id);
        } else {
          item.onPress();
        }
      }}
    >
      <CardContent
        sx={{
          p: 1,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-start',
          flexGrow: 1,
          position: 'relative',
          minHeight: 45,
        }}
      >
        <Box sx={{ 
          color: item.id === 'logout' ? '#d32f2f' : '#006AF9', 
          mr: 1, 
          display: 'flex', 
          alignItems: 'center' 
        }} className="menu-icon">
          {item.icon}
        </Box>
        <Typography
          variant="body2"
          className="menu-text"
          sx={{
            fontWeight: 600,
            color: item.id === 'logout' ? '#d32f2f' : '#333',
            fontSize: '0.8rem',
            lineHeight: 1.2,
            flexGrow: 1,
          }}
        >
          {item.title}
        </Typography>
        {item.subItems && (
          <IconButton
            size="small"
            className="expand-icon"
            sx={{
              color: '#006AF9',
              ml: 'auto',
            }}
          >
            {expandedMenu === item.id ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </CardContent>
      
      {item.subItems && (
        <Collapse in={expandedMenu === item.id}>
          <Box sx={{ p: 1, backgroundColor: '#f8f9fa' }}>
            {item.subItems.map((subItem) => (
                                            <Box
                 key={subItem.id}
                 sx={{
                   display: 'flex',
                   alignItems: 'center',
                   p: 1,
                   cursor: 'pointer',
                   borderRadius: 1,
                   transition: 'all 0.3s ease',
                   '&:hover': {
                     backgroundColor: '#0A2A66',
                     '& .submenu-icon': {
                       color: '#ffffff',
                     },
                     '& .submenu-text': {
                       color: '#ffffff',
                     },
                   },
                 }}
                 onClick={(e) => {
                   e.stopPropagation();
                   subItem.onPress();
                 }}
               >
                 <Box sx={{ color: '#006AF9', mr: 1, display: 'flex', alignItems: 'center' }} className="submenu-icon">
                   {subItem.icon}
                 </Box>
                 <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#666' }} className="submenu-text">
                   {subItem.title}
                 </Typography>
               </Box>
            ))}
          </Box>
        </Collapse>
      )}
    </Card>
  );

  const renderGroup = (groupName, items) => (
    <Box key={groupName} sx={{ mb: 2 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: '#333',
          mb: 1.5,
          fontSize: '1rem',
        }}
      >
        {groupName}
      </Typography>
      <Grid container spacing={1}>
        {items.map((item) => (
          <Grid item xs={6} sm={4} md={3} key={item.id}>
            {renderMenuItem(item)}
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // Don't render ProfileGridMenu for guests
  if (isGuest) {
    return null;
  }

  return (
    <>
      <Box sx={{ p: 0.5 }}>
        {Object.entries(groupedMenuItems).map(([groupName, items]) =>
          renderGroup(groupName, items)
        )}
      </Box>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={showLogoutDialog}
        onClose={handleCancelLogout}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)',
            border: '1px solid rgba(211, 47, 47, 0.1)',
            boxShadow: '0 20px 40px rgba(211, 47, 47, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          color: '#d32f2f',
          fontWeight: 600
        }}>
          <Warning sx={{ color: '#d32f2f' }} />
          Confirm Logout
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, color: '#666' }}>
            You have <strong>{totalItems} item{totalItems !== 1 ? 's' : ''}</strong> in your cart.
          </Typography>
          <Typography variant="body1" sx={{ color: '#666' }}>
            Logging out will clear your cart and you'll need to add items again. Are you sure you want to continue?
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCancelLogout}
            sx={{
              borderColor: '#666',
              color: '#666',
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#333',
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={performLogout}
            sx={{
              background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #c62828 0%, #d32f2f 100%)',
                boxShadow: '0 8px 20px rgba(211, 47, 47, 0.4)'
              }
            }}
          >
            Logout & Clear Cart
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProfileGridMenu; 